/**
 * Alerts API Client
 *
 * API client for alert rules and notifications
 */

import { apiService } from '@platform/api/api.service'
import type {
  AlertRule,
  AlertRuleType,
  CreateAlertRuleRequest,
  NotificationPriority,
  NotificationSeverity,
  UpdateAlertRuleRequest,
} from '@bat/shared'

import { ApiException } from '@/shared/utils/api'
import { logger } from '@/shared/utils/logger'

// ============================================================================
// TYPES
// ============================================================================

export interface UserNotification {
  id: string
  userId: string
  tenantId: string
  ruleId: string | null
  ruleName: string | null
  type: string
  title: string
  message: string
  severity: NotificationSeverity
  priority: NotificationPriority
  metadata: Record<string, unknown> | null
  actionUrl: string | null
  isRead: boolean
  readAt: Date | null
  isAcknowledged: boolean
  acknowledgedAt: Date | null
  expiresAt: Date | null
  createdAt: Date
}

interface NotificationQuery {
  unreadOnly?: boolean
  severity?: NotificationSeverity
  type?: string
  limit?: number
  offset?: number
}

export interface AlertRuleStats {
  total: number
  enabled: number
  byType: Record<string, number>
  triggeredToday: number
}

// ============================================================================
// ALERT RULES API
// ============================================================================

/** A 2xx that does not carry what the contract promises (status 0: no HTTP status describes it). */
const invalidNotificationsResponse = (what: string): ApiException =>
  new ApiException({
    status: 0,
    code: 'ALERTS_INVALID_RESPONSE',
    message: `The ${what} answer is not what the notifications API promises`,
  })

/** The notification a single-row write resolves; a 2xx without one is not a confirmed write. */
function requireNotification(
  notification: UserNotification | null | undefined,
  what: string,
): UserNotification {
  if (notification === null || notification === undefined) {
    throw invalidNotificationsResponse(what)
  }
  return notification
}

/** The two fields the bell renders and acts on; the rest of the row is the server's contract. */
const isNotificationRow = (row: unknown): boolean =>
  typeof row === 'object' &&
  row !== null &&
  typeof (row as { id?: unknown }).id === 'string' &&
  (row as { id: string }).id !== '' &&
  typeof (row as { isRead?: unknown }).isRead === 'boolean'

class AlertsApi {
  /**
   * Get all alert rules
   */
  async getAlertRules(query?: {
    enabled?: boolean
    type?: AlertRuleType
    assetSymbol?: string
  }): Promise<AlertRule[]> {
    const params = new URLSearchParams()
    if (query?.enabled !== undefined) params.append('enabled', String(query.enabled))
    if (query?.type) params.append('type', query.type)
    if (query?.assetSymbol) params.append('assetSymbol', query.assetSymbol)

    const response = await apiService.get<AlertRule[]>(
      `/api/alerts/rules${params.toString() ? `?${params.toString()}` : ''}`,
    )

    return response.data ?? []
  }

  /**
   * Get a single alert rule
   */
  async getAlertRule(id: string): Promise<AlertRule | null> {
    try {
      const response = await apiService.get<AlertRule>(`/api/alerts/rules/${id}`)
      return response.data ?? null
    } catch (error) {
      logger.error('getAlertRule failed', error)
      return null
    }
  }

  /**
   * Create a new alert rule
   */
  async createAlertRule(input: CreateAlertRuleRequest): Promise<AlertRule> {
    const response = await apiService.post<AlertRule>('/api/alerts/rules', input)
    return response.data
  }

  /**
   * Update an alert rule
   */
  async updateAlertRule(id: string, input: UpdateAlertRuleRequest): Promise<AlertRule | null> {
    try {
      const response = await apiService.put<AlertRule>(`/api/alerts/rules/${id}`, input)
      return response.data ?? null
    } catch (error) {
      logger.error('updateAlertRule failed', error)
      return null
    }
  }

  /**
   * Delete an alert rule
   */
  async deleteAlertRule(id: string): Promise<boolean> {
    try {
      await apiService.delete<{ success: boolean; data: { deleted: boolean } }>(
        `/api/alerts/rules/${id}`,
      )
      return true
    } catch (error) {
      logger.error('deleteAlertRule failed', error)
      return false
    }
  }

  /**
   * Toggle alert rule enabled status
   */
  async toggleAlertRule(id: string): Promise<AlertRule | null> {
    try {
      const response = await apiService.patch<AlertRule>(`/api/alerts/rules/${id}/toggle`)
      return response.data ?? null
    } catch (error) {
      logger.error('toggleAlertRule failed', error)
      return null
    }
  }

  /**
   * Test an alert rule
   */
  async testAlertRule(id: string): Promise<{
    triggered: boolean
    message: string
    data?: Record<string, unknown>
  }> {
    const response = await apiService.post<{
      triggered: boolean
      message: string
      data?: Record<string, unknown>
    }>(`/api/alerts/rules/${id}/test`, {})
    return response.data
  }

  /**
   * Get alert rule statistics
   */
  async getAlertRuleStats(): Promise<AlertRuleStats> {
    const response = await apiService.get<AlertRuleStats>('/api/alerts/rules/stats')
    return response.data || { total: 0, enabled: 0, byType: {}, triggeredToday: 0 }
  }

  /**
   * Bulk delete alert rules
   */
  async bulkDeleteAlertRules(ids: string[]): Promise<number> {
    try {
      const response = await apiService.post<{ deleted: number }>('/api/alerts/rules/bulk-delete', {
        ids,
      })
      return response.data?.deleted ?? 0
    } catch (error) {
      logger.error('bulkDeleteAlertRules failed', error)
      return 0
    }
  }

  /**
   * Bulk toggle alert rules
   */
  async bulkToggleAlertRules(ids: string[], enabled: boolean): Promise<number> {
    try {
      const response = await apiService.post<{ updated: number }>('/api/alerts/rules/bulk-toggle', {
        ids,
        enabled,
      })
      return response.data?.updated ?? 0
    } catch (error) {
      logger.error('bulkToggleAlertRules failed', error)
      return 0
    }
  }

  // =========================================================================
  // NOTIFICATIONS API
  // =========================================================================

  /**
   * Get user notifications
   */
  async getNotifications(query?: NotificationQuery): Promise<{
    notifications: UserNotification[]
    total: number
  }> {
    const params = new URLSearchParams()
    if (query?.unreadOnly) params.append('unreadOnly', 'true')
    if (query?.severity) params.append('severity', query.severity)
    if (query?.type) params.append('type', query.type)
    if (query?.limit) params.append('limit', String(query.limit))
    if (query?.offset) params.append('offset', String(query.offset))

    const response = await apiService.get<{
      data: UserNotification[]
      meta: { total: number }
    }>(`/api/alerts/notifications${params.toString() ? `?${params.toString()}` : ''}`)

    return {
      notifications: response.data?.data ?? [],
      total: response.data?.meta?.total ?? 0,
    }
  }

  /**
   * Get recent notifications.
   * An empty list means «the server answered with none», never «the request failed» (#4002).
   * @throws {ApiException} The error of the failed request, unchanged; or `ALERTS_INVALID_RESPONSE`
   * when the answer is not a list, or a list element lacks a non-empty `id` or a boolean `isRead`
   * (the HTTP client resolves `data: null` for a 2xx whose body is not JSON, such as the HTML page
   * of a proxy: that is a failure, not an empty inbox).
   */
  async getRecentNotifications(limit = 10): Promise<UserNotification[]> {
    const response = await apiService.get<UserNotification[]>(
      `/api/alerts/notifications/recent?limit=${limit}`,
    )
    const notifications: unknown = response.data
    // Every element is checked: the panel renders `id` and `isRead` of each one, and a `null` or a
    // row without them would throw outside the hook's catch, blanking the whole page.
    if (!Array.isArray(notifications) || !notifications.every(isNotificationRow)) {
      throw invalidNotificationsResponse('recent list')
    }
    return notifications as UserNotification[]
  }

  /**
   * Get unread count.
   * 0 means «the server counted none», never «the request failed» (#4002).
   * @throws {ApiException} The error of the failed request, unchanged; or `ALERTS_INVALID_RESPONSE`
   * when the answer carries no count that is an integer ≥ 0.
   */
  async getUnreadCount(): Promise<number> {
    const response = await apiService.get<{ count: number }>(
      '/api/alerts/notifications/unread-count',
    )
    const count: unknown = response.data?.count
    if (typeof count !== 'number' || !Number.isInteger(count) || count < 0) {
      throw invalidNotificationsResponse('unread count')
    }
    return count
  }

  /**
   * Mark notification as read.
   * @throws {ApiException} The error of the failed request, unchanged (a 404 `ALERT_002` when the
   * notification no longer exists); or `ALERTS_INVALID_RESPONSE` when a 2xx carries no notification.
   */
  async markAsRead(id: string): Promise<UserNotification> {
    const response = await apiService.patch<UserNotification>(
      `/api/alerts/notifications/${id}/read`,
    )
    return requireNotification(response.data, 'mark as read')
  }

  /**
   * Acknowledge notification.
   * @throws {ApiException} The error of the failed request, unchanged (a 404 `ALERT_002` when the
   * notification no longer exists); or `ALERTS_INVALID_RESPONSE` when a 2xx carries no notification.
   */
  async acknowledge(id: string): Promise<UserNotification> {
    const response = await apiService.post<UserNotification>(
      `/api/alerts/notifications/${id}/acknowledge`,
      {},
    )
    return requireNotification(response.data, 'acknowledge')
  }

  /**
   * Mark all notifications as read. Resolves the number marked: 0 is a success (nothing was unread,
   * for instance because another tab already marked them).
   * @throws {ApiException} The error of the failed request, unchanged; or `ALERTS_INVALID_RESPONSE`
   * when a 2xx carries no numeric `marked` count.
   */
  async markAllAsRead(): Promise<number> {
    const response = await apiService.post<{ marked: number }>(
      '/api/alerts/notifications/read-all',
      {},
    )
    const marked: unknown = response.data?.marked
    if (typeof marked !== 'number') throw invalidNotificationsResponse('mark all as read')
    return marked
  }

  /**
   * Delete notification.
   * @throws {ApiException} The error of the failed request, unchanged (a 404 `ALERT_002` when the
   * notification no longer exists); or `ALERTS_INVALID_RESPONSE` when a 2xx does not say
   * `deleted: true`.
   */
  async deleteNotification(id: string): Promise<void> {
    const response = await apiService.delete<{ deleted: boolean }>(
      `/api/alerts/notifications/${id}`,
    )
    if (response.data?.deleted !== true) throw invalidNotificationsResponse('delete')
  }

  /**
   * Delete all read notifications. Resolves the number deleted: 0 is a success.
   * @throws {ApiException} The error of the failed request, unchanged; or `ALERTS_INVALID_RESPONSE`
   * when a 2xx carries no numeric `deleted` count.
   */
  async deleteReadNotifications(): Promise<number> {
    const response = await apiService.delete<{ deleted: number }>('/api/alerts/notifications/read')
    const deleted: unknown = response.data?.deleted
    if (typeof deleted !== 'number') throw invalidNotificationsResponse('delete read')
    return deleted
  }
}

// Export singleton instance
export const alertsApi = new AlertsApi()
